import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { RevenueTrend, TrendWindow } from '@/api/types'
import { Card, EmptyState, formatCurrency, Icon, percentChange, SegmentedControl, TrendChart } from '@/design'

import { CardQueryBody } from './CardQueryBody'
import { ChangeText } from './ChangeText'
import { useDrillDown, useMoney, useOverviewQuery } from './hooks'

const WINDOWS: ReadonlyArray<{ value: TrendWindow; label: string; long: string }> = [
  { value: 'today', label: 'Today', long: 'Today' },
  { value: '7d', label: '7 days', long: 'Last 7 days' },
  { value: '30d', label: '30 days', long: 'Last 30 days' },
]

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function subtitle(series: RevenueTrend | undefined): string {
  if (series?.granularity === 'hour') return 'Hourly revenue so far today'
  // The live API has no hourly buckets: today is one daily figure, not an invented hourly curve.
  if (series?.window === 'today') return 'Revenue so far today · hourly figures are not live yet'
  return 'Daily revenue'
}

/** Whether either period has anything to draw: the API sends both series empty when neither has an order or refund. */
function hasRevenue(series: RevenueTrend): boolean {
  return series.points.some((point) => point.current !== 0 || (point.previous ?? 0) !== 0)
}

/**
 * Revenue over today, 7 or 30 days, against the comparison period (hourly
 * today on the demo; daily on the live API, which has no hourly buckets).
 * The window is the card's own; every other global filter still applies.
 */
export function RevenueTrendCard() {
  const [trendWindow, setTrendWindow] = useState<TrendWindow>('30d')
  const trend = useOverviewQuery('revenue-trend', (client, filters) => client.revenueTrend(filters, trendWindow), [trendWindow])
  const href = useDrillDown()
  const money = useMoney(trend.data?.meta.currency)
  const data = trend.data?.data
  const windowLabel = WINDOWS.find((option) => option.value === trendWindow)?.long ?? ''

  return (
    <Card
      className="ov-trend"
      title="Revenue trend"
      subtitle={subtitle(data)}
      actions={<SegmentedControl label="Trend window" options={WINDOWS} value={trendWindow} onChange={setTrendWindow} />}
      busy={trend.isFetching}
      footer={
        data ? (
          <Link className="ov-footer-link" to={href(data.drillDown)}>
            Open revenue analytics <Icon name="arrowRight" size={14} />
          </Link>
        ) : null
      }
    >
      <CardQueryBody query={trend} loadingRows={5} errorDescription="Revenue could not be loaded.">
        {({ data: series }) =>
          hasRevenue(series) ? (
            <>
              <p className="ov-trend__total">
                <span className="ov-trend__value" data-testid="trend-total">
                  {formatCurrency(series.total, money)}
                </span>
                <ChangeText change={percentChange(series.total, series.previousTotal)} label={series.compareLabel} />
                {series.comparisonNote ? <span className="ov-muted ov-trend__note">{series.comparisonNote}</span> : null}
              </p>
              {/* One point (today on the live API) is a figure, not a line: the total above says it all. */}
              {series.points.length > 1 ? (
                <TrendChart
                  title={`Revenue, ${windowLabel.toLowerCase()}`}
                  points={series.points}
                  format="currency"
                  formatOptions={money}
                  currentLabel={windowLabel}
                  previousLabel={series.compareLabel ? sentenceCase(series.compareLabel.replace(/^vs /, '')) : null}
                />
              ) : null}
            </>
          ) : (
            <EmptyState variant="no-results" compact />
          )
        }
      </CardQueryBody>
    </Card>
  )
}
